import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  createFunctionalRole,
  createRolePost,
  deleteFunctionalRole,
  deleteRolePost,
  listFunctionalRoles,
  type FunctionalRole,
} from "@/api/functionalRoles";
import { listOrganizations } from "@/api/organizations";
import { PageHeader } from "@/components/PageHeader";
import { usePermission } from "@/auth/usePermission";

/**
 * Admin screen for functional roles and the workstation each organization
 * attaches to them. A role is the operational identity of a shift (Med 1);
 * the post is what a colleague dials to reach whoever holds it.
 */
export function FunctionalRolesAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canWrite = usePermission("skill:write");

  const roles = useQuery({
    queryKey: ["functional-roles", "all"],
    queryFn: () => listFunctionalRoles(),
  });
  const orgs = useQuery({
    queryKey: ["organizations-all-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
  });

  const [showRoleForm, setShowRoleForm] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [sortOrder, setSortOrder] = useState(100);
  const [error, setError] = useState<string | null>(null);

  const [postFor, setPostFor] = useState<FunctionalRole | null>(null);
  const [postOrgId, setPostOrgId] = useState("");
  const [postPhone, setPostPhone] = useState("");
  const [postDesk, setPostDesk] = useState("");
  const [postRadio, setPostRadio] = useState("");
  const [postInstructions, setPostInstructions] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["functional-roles"] });
  const onError = (e: unknown) => {
    const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data
      ?.detail;
    setError(typeof detail === "string" ? detail : (e as Error).message);
  };

  const createRole = useMutation({
    mutationFn: () =>
      createFunctionalRole({ code: code.trim(), label: label.trim(), sort_order: sortOrder }),
    onSuccess: () => {
      setShowRoleForm(false);
      setCode("");
      setLabel("");
      setError(null);
      invalidate();
    },
    onError,
  });

  const removeRole = useMutation({
    mutationFn: (id: string) => deleteFunctionalRole(id),
    onSuccess: invalidate,
    onError,
  });

  const addPost = useMutation({
    mutationFn: () =>
      createRolePost(postFor!.id, {
        organization_id: postOrgId,
        phone_line: postPhone || null,
        desk_label: postDesk || null,
        radio_channel: postRadio || null,
        instructions: postInstructions || null,
      }),
    onSuccess: () => {
      setPostFor(null);
      setPostOrgId("");
      setPostPhone("");
      setPostDesk("");
      setPostRadio("");
      setPostInstructions("");
      setError(null);
      invalidate();
    },
    onError,
  });

  const removePost = useMutation({
    mutationFn: (v: { roleId: string; postId: string }) =>
      deleteRolePost(v.roleId, v.postId),
    onSuccess: invalidate,
    onError,
  });

  const orgName = (id: string) => {
    const o = orgs.data?.items.find((x) => x.id === id);
    return o ? `${o.code} — ${o.name}` : id.slice(0, 8);
  };

  return (
    <Box>
      <PageHeader
        title={t("functionalRoles.title")}
        subtitle={t("functionalRoles.subtitle")}
        helpTerm="functionalRole"
        actions={
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowRoleForm(true)}
            >
              {t("functionalRoles.create")}
            </Button>
          )
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {showRoleForm && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              label={t("functionalRoles.code")}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              size="small"
              required
              placeholder="MED1"
              sx={{ width: 140 }}
            />
            <TextField
              label={t("functionalRoles.label")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              size="small"
              required
              placeholder="Médecin régulateur 1"
              sx={{ flex: 1, minWidth: 220 }}
            />
            <TextField
              label={t("functionalRoles.sortOrder")}
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 100)}
              type="number"
              size="small"
              sx={{ width: 110 }}
            />
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setShowRoleForm(false)}>{t("common.cancel")}</Button>
              <Button
                variant="contained"
                disabled={!code.trim() || !label.trim() || createRole.isPending}
                onClick={() => createRole.mutate()}
              >
                {t("common.save")}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {roles.isLoading && <CircularProgress />}

      {roles.data?.length === 0 && (
        <Alert severity="info">{t("functionalRoles.empty")}</Alert>
      )}

      <Stack spacing={1.5}>
        {(roles.data ?? []).map((role) => (
          <Paper key={role.id} variant="outlined" sx={{ p: 2, opacity: role.is_active ? 1 : 0.5 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={role.code}
                  size="small"
                  color="primary"
                  sx={{ fontFamily: "monospace", fontWeight: 700 }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {role.label}
                </Typography>
                {!role.is_active && (
                  <Chip size="small" label={t("functionalRoles.inactive")} />
                )}
              </Stack>
              {canWrite && (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<PhoneIcon />}
                    onClick={() => setPostFor(role)}
                  >
                    {t("functionalRoles.addPost")}
                  </Button>
                  <Tooltip title={t("functionalRoles.deactivate")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeRole.mutate(role.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>

            {role.posts.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {t("functionalRoles.noPost")}
              </Typography>
            ) : (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {role.posts.map((post) => (
                  <Stack
                    key={post.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ pl: 1, py: 0.5, borderLeft: 2, borderColor: "divider" }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 200 }}>
                      {orgName(post.organization_id)}
                    </Typography>
                    {post.phone_line && (
                      <Chip
                        size="small"
                        icon={<PhoneIcon />}
                        label={post.phone_line}
                        sx={{ fontFamily: "monospace" }}
                      />
                    )}
                    {post.desk_label && <Chip size="small" variant="outlined" label={post.desk_label} />}
                    {post.radio_channel && (
                      <Chip size="small" variant="outlined" label={post.radio_channel} />
                    )}
                    {canWrite && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          removePost.mutate({ roleId: role.id, postId: post.id })
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>

      <Dialog open={postFor !== null} onClose={() => setPostFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("functionalRoles.postDialogTitle", { role: postFor?.code ?? "" })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>{t("settings.organization")}</InputLabel>
              <Select
                label={t("settings.organization")}
                value={postOrgId}
                onChange={(e) => setPostOrgId(e.target.value)}
              >
                {(orgs.data?.items ?? []).map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.code} — {o.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t("functionalRoles.phoneLine")}
              value={postPhone}
              onChange={(e) => setPostPhone(e.target.value)}
              placeholder="02 38 XX XX XX"
            />
            <TextField
              label={t("functionalRoles.deskLabel")}
              value={postDesk}
              onChange={(e) => setPostDesk(e.target.value)}
            />
            <TextField
              label={t("functionalRoles.radioChannel")}
              value={postRadio}
              onChange={(e) => setPostRadio(e.target.value)}
            />
            <TextField
              label={t("functionalRoles.instructions")}
              value={postInstructions}
              onChange={(e) => setPostInstructions(e.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostFor(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            disabled={!postOrgId || addPost.isPending}
            onClick={() => addPost.mutate()}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
